import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import StatsRosette from '@propertypal/shared/src/resources/icons/property/stats-rosette.svg';
import { Property } from '@propertypal/shared/src/types/property';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import { formatPropertyStats } from '@propertypal/shared/src/utils/property/chartUtils';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import LineChart from '../charts/LineChart';
import useMediaQuery from '../media-query/useMediaQuery';
import { SmallHeading, SubHeading, Text } from '../typography';
import { Container, RankingContainer, RosetteContainer, RosetteText, Wrapper } from './PropertyStats.style';

interface Props {
  property: Property;
}

const PropertyStats: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const { property } = props;
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const windowSize = useWindowSize();

  if (windowSize.width === undefined || !props.property.stats?.history) {
    return null;
  }

  const chartWidth = isMobile ? windowSize.width - 30 : Math.min(windowSize.width * 0.6, 780);
  const chartHeight = isMobile ? 350 : 450;
  const from = getFormattedPrice(property.stats?.ranking?.range?.from, '£');
  const to = getFormattedPrice(property.stats?.ranking?.range?.to, '£');
  const lineData = formatPropertyStats(property, isMobile ? 7 : 21);
  const totalDays = lineData[0].data.reduce((prev, history) => prev + (!history.hidden ? 1 : 0), 0);

  return (
    <Wrapper data-testid="property-stats">
      {!isMobile && <SubHeading mt={40}>Property Statistics</SubHeading>}

      <Container>
        {property.stats?.history && (
          <div>
            <SmallHeading fontWeight="normal" mt={30} fontSize={24}>
              Listing Views
            </SmallHeading>
            <Text fontSize={14}>(in the last {totalDays} days)</Text>

            <LineChart
              bubbleTextFormatter={(value) => `${value} Views`}
              width={chartWidth}
              height={chartHeight}
              lines={lineData}
              dottedPoints
              includeChartKeys
            />
          </div>
        )}

        {(property.stats?.totalViews || property.stats?.ranking) && (
          <RankingContainer>
            <SmallHeading fontWeight="normal" mt={30} fontSize={24} align="center">
              Total Views
            </SmallHeading>
            <Text fontSize={14} align="center">
              (since advertised)
            </Text>

            <SubHeading color={theme.primary} fontSize={54} fontWeight="normal" align="center">
              {getFormattedPrice(property.stats.totalViews, '')}
            </SubHeading>

            {property.stats.ranking && property.stats.ranking.popularity !== null && (
              <>
                <RosetteContainer>
                  <StatsRosette />
                  <RosetteText>{property.stats.ranking.popularity}</RosetteText>
                </RosetteContainer>

                <Text align="center" autoMargin fontSize={24} maxWidth={220} mt={10} mb={10}>
                  <span style={{ color: theme.primary }}>{property.stats.ranking.popularity}</span> most popular ranking
                  today in <span style={{ color: theme.primary }}>{property.stats.ranking.term}</span>
                </Text>

                {property.stats.ranking.range && (
                  <Text align="center" data-testid="rankingRangeText">
                    ({from}
                    {!property.stats.ranking.range.max ? ` - ${to}` : ''}
                    {property.stats.ranking.range.max ? '+' : ''})
                  </Text>
                )}
              </>
            )}
          </RankingContainer>
        )}
      </Container>
    </Wrapper>
  );
};

export default React.memo(PropertyStats);
